import type {
  LDContextCommon,
  LDMultiKindContext,
} from 'launchdarkly-react-client-sdk';

import type { LDContext } from '@/types/launchDarkly';
import { isWidget } from '@/utils/isWidget.web';

import { getPlatform } from './getPlatform';

export const buildLdContext = (options: LDContext): LDMultiKindContext => {
  // The randomization unit must survive a guest→login transition mid-checkout:
  // useIdentifyCustomer passes `key: customer.userId`, which would split one
  // client's funnel across two LD units. Preferring the RudderStack anonymous
  // ID keeps a single unit while email/name enrichment still applies. Web only
  // — native deliberately keeps keying by userId.
  const anonymousId =
    typeof options.anonymousId === 'string' ? options.anonymousId : undefined;

  const context: LDMultiKindContext = {
    kind: 'multi',
    platform: {
      kind: 'platform',
      key: isWidget ? 'widget' : getPlatform(),
    },
    user: {
      kind: 'user',
      key: anonymousId || options.key,
      anonymous: true,
    },
  };

  if (options.brandId) {
    context.brand = {
      kind: 'brand',
      key: options.brandId,
    };
  }

  if (options.shopId) {
    context.shop = {
      kind: 'shop',
      key: options.shopId,
    };
  }

  if (options.email) {
    context.user = {
      ...(context.user as LDContextCommon),
      anonymous: false,
      email: options.email,
      name: options.name,
    };
  }

  return context;
};

/**
 * Inverse of `buildLdContext`: turns the multi-kind context the LD SDK
 * actually stores (`{kind:'multi', user:{key,...}, brand?, shop?}`) back into
 * the flat `LDContext` shape callers merge onto via
 * `updateContext((prev) => ({...prev, shopId}))`.
 *
 * Without this, callers spreading `ldClient.getContext()` directly as `prev`
 * silently drop `key` (it lives at `user.key`, not top-level), which makes
 * `buildLdContext` build a context with no key and lets the LD SDK's
 * anonymous-context fallback mint and permanently cache an unrelated random
 * key — rebucketing the session into a different experiment arm.
 */
export const flattenLdContext = (
  context: LDMultiKindContext | null,
  fallbackKey: string,
): LDContext => {
  const user = (context?.user ?? context) as LDContextCommon | undefined;
  const brand = context?.brand as LDContextCommon | undefined;
  const shop = context?.shop as LDContextCommon | undefined;

  return {
    kind: 'user',
    key: user?.key ?? fallbackKey,
    anonymous: user?.anonymous,
    email: user?.email,
    name: user?.name,
    brandId: brand?.key,
    shopId: shop?.key,
  };
};
